import { useState } from 'react';
import { useTranslation } from 'react-i18next';

import { Spinner } from '../../components';
import { SubmittedMeter } from '../exams/ExamCharts';
import { ClassListTools } from '../results/ClassListTools';
import { filterByName } from '../results/nameSearch';
import { ResultsCardFrame } from '../results/ResultsCardFrame';
import type { ResultSession } from '../results/sessions';
import { useResultSessionStore } from '../results/store';
import { formatAverage, taskGradingStats, type GradingStats } from './gradingStats';
import { classEntries, statusLabel, taskTitleOf, vnDateTime, type RosterEntry, type StudentSubmission } from './submissions';
import { useClassSubmissions } from './useClassSubmissions';
import { useMsg } from '../../i18n/msg';

function statusClass(submission: StudentSubmission | null): string {
  if (!submission) return 'hw-status hw-status--missing';
  return submission.approved ? 'hw-status hw-status--marked' : 'hw-status hw-status--submitted';
}

// anonymous by construction: counts, an average and bands, never a name, so it shows while projected
function GradingSummary({ stats }: { stats: GradingStats }) {
  const { t } = useTranslation(['board', 'common']);
  const bands = stats.bands;
  const max = bands ? Math.max.apply(null, bands.map((band) => band.count)) : 0;
  return (
    <section className="quiz-results__block">
      <h3 className="quiz-results__block-title">{t('homeworkResults.grading')}</h3>
      <div className="hw-stats">
        <p className="hw-stats__line">
          <strong>{stats.graded}/{stats.submitted}</strong> {t('homeworkResults.gradedSuffix')}
          {stats.average !== null ? <> • {t('homeworkResults.averageLabel')} <strong>{formatAverage(stats.average)}</strong></> : null}
        </p>
        {bands ? (
          <div className="hw-stats__bars" role="img" aria-label={t('homeworkResults.bandsLabel', { bands: bands.map((band) => `${band.label}: ${band.count}`).join(', ') })}>
            {bands.map((band) => (
              <div key={band.label} className="hw-stats__band">
                <span className="hw-stats__count">{band.count}</span>
                <span className="hw-stats__track"><span className="hw-stats__fill" style={{ height: `${max > 0 ? Math.round((band.count / max) * 100) : 0}%`, background: band.color }} /></span>
                <span className="hw-stats__label">{band.label}</span>
              </div>
            ))}
          </div>
        ) : null}
      </div>
    </section>
  );
}

function HomeworkRow({ entry, active, onOpen }: { entry: RosterEntry; active: boolean; onOpen: (code: string) => void }) {
  const { t } = useTranslation(['board', 'common']);
  const submission = entry.submission;
  const body = (
    <span className="results-row__main">
      <span className="results-row__name">{entry.student.name}</span>
      <span className="results-row__sub">
        <span className={statusClass(submission)}>{statusLabel(submission)}</span>
        {submission && submission.submittedAt ? <span className="results-row__time">{vnDateTime(submission.submittedAt)}</span> : null}
      </span>
    </span>
  );
  return (
    <li className={active ? 'results-row is-active' : 'results-row'}>
      {submission ? (
        <button type="button" className="results-row__inner" aria-current={active ? 'true' : undefined} aria-label={t('homeworkResults.openAria', { name: entry.student.name, status: statusLabel(submission) })} onClick={() => onOpen(entry.student.code)}>
          {body}
          <span className="results-row__end"><span className="results-row__hint">{active ? t('homeworkResults.viewing') : t('homeworkResults.view')}</span></span>
        </button>
      ) : <div className="results-row__inner">{body}</div>}
    </li>
  );
}

export interface HomeworkResultsCardProps {
  session: ResultSession;
  /** No rail for this tab, so the card carries its own Báo cáo | Chi tiết switch. */
  switchable?: boolean;
  onBackToDe: () => void;
}

// Results for the homework on this tab: Báo cáo (counts, grading) or Chi tiết (the whole class)
export function HomeworkResultsCard({ session, switchable = false, onBackToDe }: HomeworkResultsCardProps) {
  const { t } = useTranslation(['board', 'common']);
  const tr = useMsg();
  const { roster, loading, error, tasks, courseTitles, reload } = useClassSubmissions();
  const setPanelTab = useResultSessionStore((state) => state.setPanelTab);
  const setCollapsed = useResultSessionStore((state) => state.setCollapsed);
  const hide = useResultSessionStore((state) => state.hide);
  const openStudent = useResultSessionStore((state) => state.openStudent);
  const [query, setQuery] = useState('');
  const task = tasks.filter((item) => item.id === session.refId)[0] || null;
  const onOpen = (code: string) => openStudent(session.tabId, code);

  const subtitle = [task ? tr(taskTitleOf(task.title)) : tr(session.title), task ? courseTitles[task.courseId] || '' : ''].filter(Boolean).join(' • ');
  const viewed = session.student ? roster.filter((student) => student.code === session.student)[0] : undefined;
  const studentName = session.student ? (viewed ? viewed.name : session.student) : null;
  const actions = session.student ? <button type="button" className="hw-panel__refresh" onClick={onBackToDe}>{t('homeworkResults.backToQuestion')}</button> : null;

  let body;
  if (!task) {
    body = loading || roster.length === 0 ? <Spinner label={t('homeworkResults.loading')} />
      : error ? <p className="quiz-results__empty">{t('homeworkResults.loadFailed', { error: tr(error) })} <button type="button" className="hw-panel__refresh" onClick={reload}>{t('common:actions.retry')}</button></p>
        : <p className="quiz-results__empty">{t('homeworkResults.noSubmissions')}</p>;
  } else if (session.panelTab === 'stats') {
    body = <><SubmittedMeter submitted={task.count} total={roster.length} /><GradingSummary stats={taskGradingStats(task)} /></>;
  } else {
    const entries = filterByName(classEntries(task, roster), query, (entry) => `${entry.student.name} ${entry.student.code}`);
    body = (
      <section className="quiz-results__block">
        <ClassListTools query={query} onQuery={setQuery} onReload={reload} reloading={loading} />
        <div className="results-list__head"><span>{query.trim() ? t('homeworkResults.filteredCount', { shown: entries.length, total: roster.length }) : t('homeworkResults.students', { count: roster.length })}</span><span>{t('homeworkResults.submittedCount', { count: task.count })}</span></div>
        {entries.length === 0 ? <p className="quiz-results__empty">{t('homeworkResults.noMatch')}</p> : (
          <ul className="results-list">
            {entries.map((entry) => <HomeworkRow key={entry.student.code} entry={entry} active={entry.student.code === session.student} onOpen={onOpen} />)}
          </ul>
        )}
      </section>
    );
  }

  return (
    <ResultsCardFrame kindLabel={t('homeworkResults.kindLabel')} subtitle={subtitle} actions={actions} view={session.panelTab} onView={switchable ? (view) => setPanelTab(session.tabId, view) : undefined}
      collapsed={session.collapsed} studentName={studentName} onCollapse={() => setCollapsed(session.tabId, true)} onExpand={() => setCollapsed(session.tabId, false)} onClose={() => hide(session.tabId)}>
      {body}
    </ResultsCardFrame>
  );
}
